import React, { ReactElement, useCallback, useState } from 'react';
import classnames from 'classnames';
import { Input } from 'ps-design';
import './InlineEdit.scss';

interface InlineEditProps {
  text: string;
  classNames?: string;
  onSave: Function;
  onError?: Function;
  onCancel?: Function;
  errorMessage?: string;
}

export default function InlineEdit({
  text,
  classNames,
  onSave,
  onError,
  onCancel,
  errorMessage
}: InlineEditProps): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(text);

  const startEditing = useCallback(() => {
    if (!isEditing) {
      setIsEditing(true);
      setEditedValue(text);
    }
  }, [isEditing, setIsEditing, setIsEditing, text]);

  const endEditingSave = useCallback(() => {
    if (editedValue === text) setIsEditing(false);
    else if (onSave(editedValue)) setIsEditing(false);
    else if (onError) onError(errorMessage);
  }, [onSave, editedValue, setIsEditing]);

  const endEditingCancel = useCallback(() => {
    if (onCancel) onCancel(editedValue);
    setIsEditing(false);
    setEditedValue(text);
  }, [setIsEditing, setEditedValue, text]);

  const handleValueChange = useCallback(
    (_: undefined, value: string): void => {
      setEditedValue(value);
    },
    [setEditedValue]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') endEditingSave();
    },
    [endEditingSave]
  );

  return (
    <div
      onMouseDown={startEditing}
      className={classnames('inline-edit__container', { 'is-editing': isEditing }, classNames)}
    >
      <div className="inline-edit__text-container">
        {isEditing ? <Input value={editedValue} onChange={handleValueChange} onKeyDown={handleKeyDown} /> : text}
      </div>
      {isEditing && (
        <>
          <a onClick={endEditingSave} className="pxl-btn pxl-btn--primary pxl-btn--sm inline-edit__button">
            <i className="icon-check" />
          </a>
          <a onClick={endEditingCancel} className="pxl-btn pxl-btn--primary pxl-btn--sm inline-edit__button">
            <i className="icon-cancel" />
          </a>
        </>
      )}
      {!isEditing && <i className="inline-edit__icon icon-pencil" />}
    </div>
  );
}
